import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, hasUsedProTrial, markProTrialUsed, patchAuthUser, saveSelectedPlan } from '../auth';
import { onboarding as onboardingApi } from '../services/api';

const TOTAL_STEPS = 5;
const ONBOARDING_STORAGE_KEY = 'pm_onboarding_complete';

function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    modalidade: null,
    stakes: null,
    experiencia: null,
    objetivo: null,
    tempo: '3h/semana',
  });
  const [sliderVal, setSliderVal] = useState(3);
  const [showResult, setShowResult] = useState(false);
  const [resultMsg, setResultMsg] = useState('Seu Coach IA já conhece seu perfil e está pronto para começar.');
  const [obIsAnual, setObIsAnual] = useState(true);
  const [obProPrice, setObProPrice] = useState('39');
  const [obElitePrice, setObElitePrice] = useState('79');
  const [obProPeriod, setObProPeriod] = useState('/mês · cobrado anualmente');
  const [obElitePeriod, setObElitePeriod] = useState('/mês · cobrado anualmente');
  const [obBtnProHref, setObBtnProHref] = useState('https://pay.kiwify.com.br/Ad3EqAb');
  const [obBtnEliteHref, setObBtnEliteHref] = useState('https://pay.kiwify.com.br/VyQaS2y');
  const [isSavingOnboarding, setIsSavingOnboarding] = useState(false);
  const auth = getAuth();
  const hasUsedTrial = hasUsedProTrial(auth?.user?.email);

  const progress = showResult ? 100 : (currentStep / TOTAL_STEPS) * 100;
  const stepLabel = showResult ? 5 : currentStep;

  function selectOption(key, value) {
    setAnswers(prev => ({ ...prev, [key]: value }));
  }

  function isSelected(key, value) {
    return answers[key] === value;
  }

  function nextStep(from) {
    let nextNum = from + 1;
    if (from === 1 && answers.modalidade === 'Iniciante') {
      nextNum = 5;
      setAnswers(prev => ({ ...prev, stakes: 'Não se aplica', experiencia: 'Iniciante', objetivo: 'Aprender do zero' }));
    }
    setCurrentStep(nextNum);
  }

  function prevStep(from) {
    let prevNum = from - 1;
    if (from === 5 && answers.modalidade === 'Iniciante') {
      prevNum = 1;
      setAnswers(prev => ({ ...prev, stakes: null, experiencia: null, objetivo: null }));
    }
    setCurrentStep(prevNum);
  }

  function skipToPlans() {
    setCurrentStep(5);
    setShowResult(true);
  }

  function buildOnboardingState() {
    const finalTempo = (sliderVal >= 20 ? '20+' : sliderVal) + 'h/semana';
    const finalAnswers = { ...answers, tempo: finalTempo };
    const payload = {
      game_type: finalAnswers.modalidade || 'MTT',
      stakes: finalAnswers.stakes || 'Não se aplica',
      experience: finalAnswers.experiencia || 'Não informado',
      goal: finalAnswers.objetivo || 'Não informado',
      frequency: finalTempo,
    };

    return { finalAnswers, payload };
  }

  async function persistOnboarding() {
    if (isSavingOnboarding) return null;

    const { finalAnswers, payload } = buildOnboardingState();
    setIsSavingOnboarding(true);

    try {
      await onboardingApi.saveOnboarding(payload);
      localStorage.setItem('pm_profile', JSON.stringify(finalAnswers));
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
      patchAuthUser({ onboarding_done: 1 });
      return finalAnswers;
    } finally {
      setIsSavingOnboarding(false);
    }
  }

  async function finishOnboarding() {
    let finalAnswers;

    try {
      finalAnswers = await persistOnboarding();
    } catch (error) {
      alert(error.message || 'Nao foi possivel salvar seu onboarding. Tente novamente.');
      return;
    }

    if (!finalAnswers) return;

    let msg = 'Seu Coach IA já conhece seu perfil e está pronto para começar. Sua primeira missão foi gerada com base no tempo disponível.';
    if (finalAnswers.modalidade && finalAnswers.experiencia) {
      msg = `Coach IA configurado para ${finalAnswers.modalidade} — nível ${finalAnswers.experiencia}. Sua missão de estudo foi gerada.`;
    }
    setResultMsg(msg);
    setShowResult(true);
  }

  async function goToDashboard() {
    try {
      const persisted = await persistOnboarding();
      if (!persisted) return;
    } catch (error) {
      alert(error.message || 'Nao foi possivel salvar seu onboarding. Tente novamente.');
      return;
    }

    saveSelectedPlan('free');
    patchAuthUser({ onboarding_done: 1, plan: 'free' });
    navigate('/dashboard');
  }

  async function handlePlanSelection(plan) {
    try {
      const persisted = await persistOnboarding();
      if (!persisted) return false;
    } catch (error) {
      alert(error.message || 'Nao foi possivel salvar seu onboarding. Tente novamente.');
      return false;
    }

    saveSelectedPlan(plan);
    patchAuthUser({ onboarding_done: 1, plan });
    return true;
  }

  async function handlePaidPlanRedirect(event, plan, href) {
    event.preventDefault();
    if (isSavingOnboarding) return;

    const success = await handlePlanSelection(plan);
    if (!success) return;

    if (plan === 'pro' && !hasUsedTrial) {
      markProTrialUsed(auth?.user?.email);
    }

    window.open(href, '_blank', 'noopener,noreferrer');
  }

  function backFromResult() {
    setShowResult(false);
    setCurrentStep(5);
  }

  function obToggleBilling() {
    const next = !obIsAnual;
    setObIsAnual(next);
    setObProPrice(next ? '39' : '49');
    setObElitePrice(next ? '79' : '99');
    setObProPeriod(next ? '/mês · cobrado anualmente' : '/mês · cobrado mensalmente');
    setObElitePeriod(next ? '/mês · cobrado anualmente' : '/mês · cobrado mensalmente');
    setObBtnProHref(next ? 'https://pay.kiwify.com.br/Ad3EqAb' : 'https://pay.kiwify.com.br/TsIQh1F');
    setObBtnEliteHref(next ? 'https://pay.kiwify.com.br/VyQaS2y' : 'https://pay.kiwify.com.br/bkqOAxu');
  }

  function OptionCard({ field, value, icon, title, desc, disabled }) {
    const selected = isSelected(field, value);
    return (
      <div
        className={`ob-option-card${selected ? ' selected' : ''}${disabled ? '' : ''}`}
        style={disabled ? {opacity: 0.4, pointerEvents: 'none', cursor: 'not-allowed'} : {}}
        onClick={() => !disabled && selectOption(field, value)}
      >
        <div className="ob-option-icon">{icon}</div>
        <div className="ob-option-text">
          <strong>{title}</strong>
          {desc && <span>{desc}</span>}
        </div>
        <div className="ob-option-check">✓</div>
      </div>
    );
  }

  const isBeginnerFlow = answers.modalidade === 'Iniciante';

  return (
    <div className="onboarding-page">
      <div className="bg-glow" />

      {/* HEADER */}
      <div className="ob-header">
        <Link to="/" className="ob-header-logo">
          <img src={require('../logo.png')} alt="PokerMind Brasil" style={{ height: '46px', objectFit: 'contain' }} />
        </Link>
        <div className="ob-header-step">Passo <span>{stepLabel}</span> de 5</div>
        <button className="ob-header-skip" onClick={skipToPlans}>Pular →</button>
      </div>

      {/* PROGRESS */}
      <div className="ob-progress-wrap">
        <div className="ob-progress-bar" style={{width: `${progress}%`}} />
      </div>

      {/* MAIN */}
      <div className="ob-main">
        <div className="ob-step-wrap">

          {/* PASSO 1 */}
          <div className={`ob-step${currentStep === 1 && !showResult ? ' active' : ''}`}>
            <div className="ob-step-num">Pergunta 1 de 5</div>
            <h1 className="ob-step-title">Qual modalidade<br />você mais joga?</h1>
            <p className="ob-step-sub">Vamos personalizar seu Coach IA e plano de estudo para o seu formato.</p>
            <div className="ob-options-grid cols-1" style={{marginBottom: '10px'}}>
              <OptionCard field="modalidade" value="MTT" title="MTT" desc="Torneios multi-mesa"
                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>}
              />
              <OptionCard field="modalidade" value="Iniciante" title="Sou iniciante — ainda estou aprendendo" desc="Nunca joguei ou estou começando do zero agora"
                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L9.1 9.1 2 12l7.1 2.9L12 22l2.9-7.1L22 12l-7.1-2.9z"/></svg>}
              />
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '40px'}}>
              {[
                {title: 'Cash Game', desc: 'Mesas de dinheiro real', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="12" x="2" y="6" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>},
                {title: 'Spin & Go', desc: 'Torneios hiperturbo', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/><circle cx="9" cy="10" r="1"/><circle cx="15" cy="10" r="1"/><circle cx="12" cy="10" r="1"/></svg>},
                {title: 'SNG', desc: 'Sit & Go', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>},
              ].map(item => (
                <div key={item.title} className="ob-option-card" style={{opacity: 0.4, pointerEvents: 'none', cursor: 'not-allowed', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '8px'}}>
                  <div className="ob-option-icon">{item.icon}</div>
                  <div className="ob-option-text" style={{textAlign: 'center'}}>
                    <strong style={{display: 'block', marginBottom: '2px'}}>{item.title}</strong>
                    <span style={{display: 'block', marginBottom: '4px'}}>{item.desc}</span>
                    <span className="em-breve-badge">Em breve</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="ob-step-nav">
              <button className="ob-btn-back" disabled>← Voltar</button>
              <button className="ob-btn-next" onClick={() => nextStep(1)} disabled={!answers.modalidade}>Próximo →</button>
            </div>
          </div>

          {/* PASSO 2 */}
          <div className={`ob-step${currentStep === 2 && !showResult ? ' active' : ''}`}>
            <div className="ob-step-num">Pergunta 2 de 5</div>
            <h1 className="ob-step-title">Qual nível de<br />stakes você joga?</h1>
            <p className="ob-step-sub">Isso calibra a complexidade das análises e recomendações do Coach IA.</p>
            <div className="ob-options-grid cols-1">
              {[
                {value: 'Micro', title: 'Micro Stakes', desc: 'NL2–NL10 / buy-ins abaixo de R$20', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 20h10"/><path d="M10 20c5.5-2.5.8-6.4 3-10"/><path d="M9.5 9.4c1.1.8 1.8 2.1 2.6 3.3a15.6 15.6 0 0 0 1.7 2.1"/><path d="M14.1 6A7 7 0 0 0 4 16.4"/></svg>},
                {value: 'Low', title: 'Low Stakes', desc: 'NL25–NL100 / buy-ins R$20–R$100', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>},
                {value: 'Mid', title: 'Mid Stakes', desc: 'NL200–NL500 / buy-ins R$100–R$500', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12l4 6-10 13L2 9Z"/><path d="M11 3 8 9l4 13 4-13-3-6"/><path d="M2 9h20"/></svg>},
                {value: 'High', title: 'High Stakes+', desc: 'NL1000+ / buy-ins acima de R$500', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>},
              ].map(item => (
                <OptionCard key={item.value} field="stakes" value={item.value} title={item.title} desc={item.desc} icon={item.icon} />
              ))}
            </div>
            <div className="ob-step-nav">
              <button className="ob-btn-back" onClick={() => prevStep(2)}>← Voltar</button>
              <button className="ob-btn-next" onClick={() => nextStep(2)} disabled={!answers.stakes}>Próximo →</button>
            </div>
          </div>

          {/* PASSO 3 */}
          <div className={`ob-step${currentStep === 3 && !showResult ? ' active' : ''}`}>
            <div className="ob-step-num">Pergunta 3 de 5</div>
            <h1 className="ob-step-title">Qual seu nível<br />de experiência?</h1>
            <p className="ob-step-sub">Sem julgamentos — só queremos entregar o conteúdo certo para o seu momento.</p>
            <div className="ob-options-grid cols-1">
              {[
                {value: 'Iniciante', title: 'Iniciante', desc: 'Conheço as regras mas estou aprendendo estratégia', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 8h.01"/><path d="M18 8h.01"/><path d="M12 8h.01"/><path d="M6 12h12"/><path d="M6 16h.01"/><path d="M18 16h.01"/><path d="M12 16h.01"/></svg>},
                {value: 'Intermediário', title: 'Intermediário', desc: 'Jogo há algum tempo, entendo GTO básico e rastreadores', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>},
                {value: 'Avançado', title: 'Avançado', desc: 'Uso solvers, estudo spots e tenho resultados consistentes', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/></svg>},
                {value: 'Profissional', title: 'Profissional', desc: 'Vivo do poker ou estou em processo de profissionalização', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="14" r="8"/><path d="M8 2h8"/><path d="M9 2v2.789a4 4 0 0 1-.672 2.219l-.656.984A4 4 0 0 0 7 10.212V12"/><path d="m15 2-.047 2.789A4 4 0 0 0 15.625 7l.656.984A4 4 0 0 1 17 10.212V12"/><path d="M12 14v4"/><path d="m9 18 3-2 3 2"/></svg>},
              ].map(item => (
                <OptionCard key={item.value} field="experiencia" value={item.value} title={item.title} desc={item.desc} icon={item.icon} />
              ))}
            </div>
            <div className="ob-step-nav">
              <button className="ob-btn-back" onClick={() => prevStep(3)}>← Voltar</button>
              <button className="ob-btn-next" onClick={() => nextStep(3)} disabled={!answers.experiencia}>Próximo →</button>
            </div>
          </div>

          {/* PASSO 4 */}
          <div className={`ob-step${currentStep === 4 && !showResult ? ' active' : ''}`}>
            <div className="ob-step-num">Pergunta 4 de 5</div>
            <h1 className="ob-step-title">Qual seu principal<br />objetivo agora?</h1>
            <p className="ob-step-sub">Isso define a missão do dia e a trilha de estudo que o Coach IA vai gerar para você.</p>
            {!isBeginnerFlow ? (
              <div className="ob-options-grid cols-2">
                {[
                  {value: 'Parar de perder dinheiro', title: 'Parar de perder', desc: 'Estabilizar os resultados', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>},
                  {value: 'Subir de stakes', title: 'Subir de stakes', desc: 'Evoluir para o próximo nível', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>},
                  {value: 'Entender GTO e matemática', title: 'Entender GTO', desc: 'Dominar a matemática do poker', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>},
                  {value: 'Me profissionalizar', title: 'Me profissionalizar', desc: 'Viver do poker', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>},
                ].map(item => (
                  <OptionCard key={item.value} field="objetivo" value={item.value} title={item.title} desc={item.desc} icon={item.icon} />
                ))}
              </div>
            ) : (
              <div className="ob-options-grid cols-2">
                {[
                  {value: 'Aprender as regras e fundamentos', title: 'Aprender do zero', desc: 'Regras, mãos e fundamentos do jogo', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>},
                  {value: 'Jogar com amigos sem passar vergonha', title: 'Jogar com amigos', desc: 'Entender o suficiente para se divertir', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>},
                  {value: 'Começar a ganhar dinheiro com poker', title: 'Ganhar dinheiro', desc: 'Quero jogar online com lucro', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>},
                  {value: 'Entender como o poker funciona matematicamente', title: 'Entender a matemática', desc: 'Probabilidades, EV e estratégia', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="10" y2="18"/><line x1="14" y1="18" x2="16" y2="18"/></svg>},
                ].map(item => (
                  <OptionCard key={item.value} field="objetivo" value={item.value} title={item.title} desc={item.desc} icon={item.icon} />
                ))}
              </div>
            )}
            <div className="ob-step-nav">
              <button className="ob-btn-back" onClick={() => prevStep(4)}>← Voltar</button>
              <button className="ob-btn-next" onClick={() => nextStep(4)} disabled={!answers.objetivo}>Próximo →</button>
            </div>
          </div>

          {/* PASSO 5 */}
          <div className={`ob-step${currentStep === 5 && !showResult ? ' active' : ''}`}>
            <div className="ob-step-num">Pergunta 5 de 5</div>
            <h1 className="ob-step-title">Quanto tempo você<br />estuda por semana?</h1>
            <p className="ob-step-sub">O Coach IA vai montar um plano realista baseado no tempo que você tem disponível.</p>
            <div className="ob-slider-wrap">
              <div className="ob-slider-val"><span>{sliderVal >= 20 ? '20+' : sliderVal}</span> <small>horas/semana</small></div>
              <div className="ob-slider-labels"><span>1h</span><span>5h</span><span>10h</span><span>20h+</span></div>
              <input type="range" min="1" max="20" value={sliderVal}
                onChange={e => setSliderVal(parseInt(e.target.value))} />
            </div>
            <div className="ob-step-nav">
              <button className="ob-btn-back" onClick={() => prevStep(5)}>← Voltar</button>
              <button className="ob-btn-next" onClick={finishOnboarding} disabled={isSavingOnboarding}>{isSavingOnboarding ? 'Salvando...' : 'Gerar meu plano →'}</button>
            </div>
          </div>

          {/* RESULTADO */}
          <div className={`ob-step${showResult ? ' active' : ''}`} style={{maxWidth: '800px', margin: '0 auto'}}>
            <div style={{textAlign: 'center', marginBottom: '36px'}}>
              <div className="ob-result-icon">✓</div>
              <h2 className="ob-result-title">Perfil criado!</h2>
              <p className="ob-result-sub">{resultMsg}</p>
            </div>

            {/* Toggle mensal/anual */}
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginBottom: '32px'}}>
              <span style={{fontSize: '0.88rem', fontWeight: 500, color: obIsAnual ? 'var(--muted)' : 'var(--white)'}}>Mensal</span>
              <div onClick={obToggleBilling} style={{width: '52px', height: '28px', borderRadius: '14px', background: 'var(--yellow)', cursor: 'pointer', position: 'relative', flexShrink: 0}}>
                <div style={{width: '22px', height: '22px', borderRadius: '50%', background: '#000', position: 'absolute', top: '3px', left: '3px', transition: 'transform .25s', transform: obIsAnual ? 'translateX(24px)' : 'translateX(0)'}} />
              </div>
              <span style={{fontSize: '0.88rem', fontWeight: 500, color: obIsAnual ? 'var(--white)' : 'var(--muted)'}}>
                Anual <span style={{background: 'rgba(74,222,128,.15)', color: 'var(--green)', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', marginLeft: '4px'}}>-20%</span>
              </span>
            </div>

            {/* Pricing cards */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px', marginBottom: '24px'}}>
              {/* Free */}
              <div style={{background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div style={{fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '0.78rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '8px'}}>Free</div>
                <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '2px', lineHeight: 1, marginBottom: '4px'}}>
                  <span style={{fontSize: '1rem', color: 'var(--muted)', marginTop: '5px'}}>R$</span>
                  <span style={{fontFamily: "'Barlow', sans-serif", fontWeight: 600, fontSize: '2.8rem', letterSpacing: '-.02em'}}>0</span>
                </div>
                <div style={{fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '16px'}}>para sempre</div>
                <ul style={{listStyle: 'none', textAlign: 'left', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', flex: 1}}>
                  <li style={{fontSize: '0.78rem', color: 'var(--muted)', display: 'flex', gap: '8px'}}><span style={{color: 'var(--green)'}}>✓</span>Coach IA — 5 msgs/dia</li>
                  <li style={{fontSize: '0.78rem', color: 'var(--muted)', display: 'flex', gap: '8px'}}><span style={{color: 'var(--green)'}}>✓</span>3 análises de torneio</li>
                  <li style={{fontSize: '0.78rem', color: 'var(--muted)', display: 'flex', gap: '8px'}}><span style={{color: 'var(--green)'}}>✓</span>Dashboard básico</li>
                </ul>
                <button
                  type="button"
                  onClick={goToDashboard}
                  style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '11px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '10px', color: 'var(--white)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '0.85rem', letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none', cursor: 'pointer'}}
                >
                  Continuar grátis
                </button>
              </div>

              {/* Pro */}
              <div style={{background: 'linear-gradient(160deg,rgba(245,200,0,.07),var(--card) 60%)', border: '1.5px solid var(--yellow)', borderRadius: '16px', padding: '24px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative'}}>
                <div style={{position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', padding: '3px 12px', background: 'var(--yellow)', color: '#000', fontFamily: "'Barlow', sans-serif", fontWeight: 800, fontSize: '0.65rem', letterSpacing: '.06em', textTransform: 'uppercase', borderRadius: '4px', whiteSpace: 'nowrap'}}>⭐ Recomendado</div>
                <div style={{fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '0.78rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--yellow)', marginBottom: '8px'}}>Pro</div>
                <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '2px', lineHeight: 1, marginBottom: '4px'}}>
                  <span style={{fontSize: '1rem', color: 'var(--muted)', marginTop: '5px'}}>R$</span>
                  <span style={{fontFamily: "'Barlow', sans-serif", fontWeight: 600, fontSize: '2.8rem', letterSpacing: '-.02em'}}>{obProPrice}</span>
                </div>
                <div style={{fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '16px'}}>{obProPeriod}</div>
                <ul style={{listStyle: 'none', textAlign: 'left', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', flex: 1}}>
                  {['Coach IA — 25 msgs/dia', 'Análises ilimitadas', 'Plano de estudo por IA', '7 perfis de vilão', 'Biblioteca de aulas'].map(f => (
                    <li key={f} style={{fontSize: '0.78rem', color: 'var(--white)', display: 'flex', gap: '8px'}}><span style={{color: 'var(--yellow)'}}>✓</span>{f}</li>
                  ))}
                </ul>
                <a href={obBtnProHref} target="_blank" rel="noopener noreferrer" onClick={(event) => handlePaidPlanRedirect(event, 'pro', obBtnProHref)} style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px', background: 'var(--yellow)', border: 'none', borderRadius: '10px', color: '#000', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: '0.88rem', letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none', textAlign: 'center', lineHeight: 1.3}}>{hasUsedTrial ? 'ASSINAR PRO' : <>TESTAR GRÁTIS POR<br />7 DIAS</>}</a>
                {!hasUsedTrial && <div style={{fontSize: '0.65rem', color: 'rgba(255,255,255,.3)', marginTop: '6px'}}>Sem cartão de crédito</div>}
              </div>

              {/* Elite */}
              <div style={{background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div style={{fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '0.78rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '8px'}}>Elite</div>
                <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '2px', lineHeight: 1, marginBottom: '4px'}}>
                  <span style={{fontSize: '1rem', color: 'var(--muted)', marginTop: '5px'}}>R$</span>
                  <span style={{fontFamily: "'Barlow', sans-serif", fontWeight: 600, fontSize: '2.8rem', letterSpacing: '-.02em'}}>{obElitePrice}</span>
                </div>
                <div style={{fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '16px'}}>{obElitePeriod}</div>
                <ul style={{listStyle: 'none', textAlign: 'left', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', flex: 1}}>
                  {['Tudo do Pro', 'Masterclasses ao vivo', 'Análise avançada', 'Suporte prioritário', 'Comunidade Elite'].map(f => (
                    <li key={f} style={{fontSize: '0.78rem', color: 'var(--muted)', display: 'flex', gap: '8px'}}><span style={{color: 'var(--green)'}}>✓</span>{f}</li>
                  ))}
                </ul>
                <a href={obBtnEliteHref} target="_blank" rel="noopener noreferrer" onClick={(event) => handlePaidPlanRedirect(event, 'elite', obBtnEliteHref)} style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '11px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '10px', color: 'var(--white)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '0.85rem', letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none'}}>Assinar Elite</a>
              </div>
            </div>

            <div style={{textAlign: 'center', fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '24px'}}>Todos os planos incluem acesso pelo navegador, sem instalação. Cancele quando quiser.</div>
            <div style={{display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap'}}>
              <button
                className="ob-btn-next"
                onClick={goToDashboard}
                disabled={isSavingOnboarding}
                style={{minWidth: '220px'}}
              >
                {isSavingOnboarding ? 'Salvando...' : 'Ir para meu dashboard →'}
              </button>
              <button className="ob-btn-back" onClick={backFromResult} style={{margin: '0 auto'}}>← Voltar às perguntas</button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Onboarding;
